import { types, flow, getEnv, SnapshotOut, Instance } from 'mobx-state-tree';
import { IChatPerson, IChatMessages, IChatMessage, InsertMessageOperationResult, ChatInsertMessageResult, IBlockedPerson } from '../../dtos/chatDtos';
import { ProgressStateModel, ProgressStateModelForStore } from '../progressStateModel';
import { createDateFormaterSvc } from '../../services/dateFormaterSvc';
import { observable } from 'mobx';
import { ICommonAppEnvWithSBBaseApi } from '../index';
import { translationForMts } from 'easit--localization';
import { Autolinker } from './autolinker';

export const ChatListItem = types
    .model('chatListItem', {
        id: types.string,
        senderId: types.number,
        nickname: types.string,
        isSenderBlocked: types.boolean,
        replyNickname: types.maybeNull(types.string),
        time: types.Date,
        message: types.string,
    }).actions(self => {
        return {
            setIsSenderBlocked: (value: boolean) => {
                self.isSenderBlocked = value;
            }
        }
    });

export const ChatResponseSourceStore = types
    .model("chatResponseSource", {
        messageId: types.string,
        personId: types.number,
        nickname: types.string,
    });

const ChatListKey = 'chatList';
export const ChatListStore = types
    .model(ChatListKey, {
        state: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
        messages: types.optional(types.array(ChatListItem), []),
        writtenMessage: types.optional(types.string, ""),
        hasPersonNickname: types.maybeNull(types.boolean),
        errorMessage: types.maybeNull(types.string),
        isSending: types.optional(types.boolean, false),
        isPrivate: types.optional(types.boolean, false),
        personId: types.optional(types.number, 0),
        isBlockedPrivateMessage: types.optional(types.boolean, false),
        blockAllSendingInfoIsLoading: types.optional(types.boolean, false),
        writingResponseOf: types.maybeNull(ChatResponseSourceStore),
        blockedPersons: types.optional(types.map(types.number), {}),
        isBlockChangeSendingMap: types.optional(types.map(types.number), {}),
        canShowChat: types.optional(types.boolean, false)
    })
    .views(self => {
        const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);
        return {
            get isAllowed(): boolean {
                return env.loggedUserId > 0;
            }
        }
    })
    .actions(self => {

        const dateFormatter = createDateFormaterSvc();
        const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);
        const repeatProtectionSeconds = 10; //in legacy in web config
        const setDataFromSubscribe = (messagesAll: IChatMessages) => {
            setDataCommon(true, messagesAll.Messages, null, messagesAll.Person);
        }
        const t = translationForMts();
        let forceTicketPublicLinkAfterLoad: { hash: string, isLegacy: boolean } | null = null;

        const setDataFromChange = (messagesAll: IChatMessage[], userMessages: IChatMessage[]) => {
            setDataCommon(false, messagesAll, userMessages, null);
        }

        const setDataCommon = (isInitial: boolean, messagesAll: IChatMessage[], userMessages: IChatMessage[], person: IChatPerson): void => {
            try {

                if (isInitial) {
                    loadPerson(person);
                }

                let finalArr = messagesAll.map(m => mapMessage(m));

                if (userMessages) {
                    userMessages.forEach(m => { finalArr.push(mapMessage(m)); });
                }

                finalArr.sort((v1, v2) => v2.time > v1.time ? 1 : (v1.time < v2.time ? -1 : 0)); // sort to desc order by time

                self.messages = observable(finalArr);

                self.state = ProgressStateModel.Success;
                self.errorMessage = '';
                if (forceTicketPublicLinkAfterLoad) {
                    addTicketPublicLink(forceTicketPublicLinkAfterLoad.hash, forceTicketPublicLinkAfterLoad.isLegacy);
                    forceTicketPublicLinkAfterLoad = null;
                }
            }
            catch (e) {
                env.loggingService.error(e);
                if (isInitial) {
                    self.state = ProgressStateModel.Failed;
                    self.errorMessage = t.chat_ErrorLoadingMessage;
                }
            }
        };

        const setErrorWhenSubscribing = (): void => {
            if (self.state === ProgressStateModel.Loading) {
                self.state = ProgressStateModel.Failed;
                self.errorMessage = t.chat_ErrorLoadingMessage;
            }
        }

        const autoLinker = new Autolinker({ className: "messageLink", email: false, truncate: 25 }); //as it was used in legacy chat
        const mapMessage = (m: IChatMessage): Instance<typeof ChatListItem> => {

            let message = replaceCouponIdentifiers(m.Text);
            message = autoLinker.link(message,);
            message = replaceEmoticons(message);

            return (<Instance<typeof ChatListItem>>{
                id: m.MessageID.toString(),
                senderId: m.PersonID,
                nickname: m.PersonNickName,
                replyNickname: m.ReplyPersonNickName,
                time: dateFormatter.toDate(m.Created),
                message: message,
                isSenderBlocked: self.blockedPersons.has(m.PersonID.toString()) //same in loadBlockedPersons
            });
        }

        const loadPerson = (person: IChatPerson): void => {
            self.personId = person.PersonID;
            self.hasPersonNickname = person.HasNickName;
            loadBlockedPersons(person.BlockedPersons);
        }

        const loadBlockedPersons = (blockedPersons: IBlockedPerson[]): void => {
            self.blockedPersons.clear();
            if (blockedPersons) {
                blockedPersons.forEach(item => {
                    self.blockedPersons.set(item.BlockedPersonID.toString(), 1);
                });
            }
            self.messages.forEach(msg => msg.setIsSenderBlocked(self.blockedPersons.has(msg.senderId.toString()))) //same in mapMessage
        }

        const addEmoticon = (symbol: string): void => {
            self.writtenMessage = self.writtenMessage + " " + symbol + " ";
        }

        const setWrittenMessage = (message: string): void => {
            self.writtenMessage = message;
        }

        const sendMessageAsync: () => Promise<void> = flow(function* () {

            let message = self.writtenMessage.trim();
            if (message.length > 0 && !self.isSending /* for sure, should be disabled by UI */) {
                try {
                    self.isSending = true;
                    self.errorMessage = null;
                    let response: InsertMessageOperationResult;
                    if (self.writingResponseOf) {
                        if (self.writingResponseOf.personId == env.loggedUserId) {
                            return;
                        }
                        message = message.replace('[' + self.writingResponseOf.nickname + ']:', "");
                        response = yield env.sRNotificationHandler.sendChatReplyMessageAsync(message, self.writingResponseOf.messageId, self.isPrivate);

                    } else {
                        response = yield env.sRNotificationHandler.sendChatMessageAsync(message);
                    }
                    if (response.Result === ChatInsertMessageResult.Success) {
                        self.errorMessage = null;
                        self.writtenMessage = "";
                    } else {
                        self.errorMessage = getInsertMessageUserError(response.Result);
                    }
                }
                catch (e) {
                    env.loggingService.error(e);
                    self.errorMessage = t.chat_ChatSendMessage_Fail; //general
                }
                finally {
                    self.isSending = false;
                }
            }
        });

        const toggleBlockAllPrivateAsync: () => Promise<void> = flow(function* () {
            self.isBlockedPrivateMessage = !self.isBlockedPrivateMessage;
            self.blockAllSendingInfoIsLoading = true;
            try {
                let response: IChatPerson = yield env.sRNotificationHandler.setBlockAllPrivateAsync(self.isBlockedPrivateMessage);
                loadPerson(response);
            } catch (e) {
                env.loggingService.error(e);
            }
            self.blockAllSendingInfoIsLoading = false;
        });

        const togglePrivate = (): void => {
            self.isPrivate = !self.isPrivate;
        }

        const setReplyTarget = (id: string, senderId: number, nickname: string): void => {
            self.writingResponseOf = ChatResponseSourceStore.create(<SnapshotOut<typeof ChatResponseSourceStore>>{
                messageId: id, personId: senderId, nickname: nickname
            });
            /*var currentMessage = self.writtenMessage;
            currentMessage = currentMessage.replace(/\[(.+?)\]:\s/g, '');
            self.writtenMessage = '[' + nickname + ']: ' + currentMessage;*/
        }

        const clearReplayTarget = (): void => {
            self.writingResponseOf = null;
        }

        const toggleBlockedPersonAsync: (messageId: string, senderId: number) => Promise<void> = flow(function* (messageId: string, senderId: number) {
            let value = !(self.blockedPersons.has(senderId.toString()));
            self.isBlockChangeSendingMap.set(messageId, 1);
            try {
                let response: IBlockedPerson[] = yield env.sRNotificationHandler.setBlockedPersonAsync(senderId, value);
                loadBlockedPersons(response);
            } catch (e) {
                env.loggingService.error(e);
            } finally {
                self.isBlockChangeSendingMap.delete(messageId);
            }
        });

        const getInsertMessageUserError = (result: ChatInsertMessageResult): string => {
            switch (result) {
                case ChatInsertMessageResult.Success: return t.chat_ChatSendMessage_Success;
                //case ChatInsertMessageResult.Fail: is default below
                case ChatInsertMessageResult.NonExistentReply: return t.chat_ChatSendMessage_NonExistentReply;
                case ChatInsertMessageResult.PrivateBlocked: return t.chat_ChatSendMessage_PrivateBlocked;
                case ChatInsertMessageResult.TimeConstraintFailed: return t.chat_ChatSendMessage_TimeConstraintFailed.replace("%1", repeatProtectionSeconds.toString());
                case ChatInsertMessageResult.UserBanned: return t.chat_ChatSendMessage_UserBanned;
                case ChatInsertMessageResult.UserNotLogged: return t.chat_ChatSendMessage_UserNotLogged;
            }
            return t.chat_ChatSendMessage_Fail;
        }

        const setIsLoading = (): void => {
            self.state = ProgressStateModel.Loading;
        }
        const couponIcon = require('../../../../modules/eTipDesign/assets/img/chat/chat/chat_coupon.png');
        const emoticons = __IsMTSTipos__ ? {
            ':D': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_01.png'),
            ':-D': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_02.png'),
            '=D': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_03.png'),
            'xD': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_04.png'),
            ":'‑)": require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_05.png'),
            'O:)': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_07.png'),
            '8-)': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_08.png'),
            '(-:': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_09.png'),
            '(:': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_09.png'),
            ';-)': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_10.png'),
            ';)': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_10.png'),
            '=)': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_11.png'),
            '=P': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_12.png'),
            ':P': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_13.png'),
            'xP': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_14.png'),
            ':‑P': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_15.png'),
            ':/P': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_16.png'),
            ':-l': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_17.png'),
            ':l': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_18.png'),
            '%‑)': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_19.png'),
            ':(': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_20.png'),
            ':-(': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_20.png'),
            '=/': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_21.png'),
            'O_O': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_23.png'),
            ":'(": require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_24.png'),
            ':-$': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_25.png'),
            ':‑|': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/tipos/chat_emoticon_tipos_26.png'),
        }
            :
            {
                ':-D': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_001.png'),
                ':D': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_001.png'),
                ':-)': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_002.png'),
                ':)': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_002.png'),
                ':-P': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_003.png'),
                ':-p': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_003.png'),
                ':P': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_003.png'),
                ':p': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_003.png'),
                ';-)': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_004.png'),
                ';)': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_004.png'),
                ':-(': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_005.png'),
                ':(': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_005.png'),
                '>:o': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_006.png'),
                '^_^': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_007.png'),
                '8-|': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_008.png'),
                '8ol': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_009.png'),
                ':-/': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_010.png'),
                ':/': require('../../../../modules/eTipDesign/assets/img/chat/emoticons/chat_emoticon_synot_010.png')
            };
        const emoticonsPatterns = [];
        const emoticonsMetachars = /[[\]{}()*+?.\\|^$\-,&#\s]/g;

        // build a regex pattern for each defined property
        for (var i in emoticons) {
            if (emoticons.hasOwnProperty(i)) { // escape metacharacters
                emoticonsPatterns.push('(' + i.replace(emoticonsMetachars, "\\$&") + ')(?![^<]*>|[^<>]*<\/)');
            }
        }

        const emoticonsFinalRegExp = new RegExp(emoticonsPatterns.join('|'), 'g');

        const replaceEmoticons = (text: string): string => {
            // build the regular expression and replace
            return text.replace(emoticonsFinalRegExp, function (match) {
                return typeof emoticons[match] != 'undefined' ?
                    '<img src="' + emoticons[match] + '"/>' :
                    match;
            });
        };


        const replaceCouponIdentifiers = (text: string) => {
            text = text.replace(new RegExp('\\[\\[:([A-Za-z0-9]{32})\\]\]', 'g'), function (match) {
                return `<a href="${env.routing.getLegacyPublicTicketPath(match.substring(3, 35))}" target="_blank"><img src="${couponIcon}"/></a>`;
            });
            text = text.replace(new RegExp('\\[\\[::([A-Za-z0-9]{32})\\]\]', 'g'), function (match) {
                return `<a href="${env.routing.getPublicTicketPath(match.substring(4, 36))}" target="_blank"><img src="${couponIcon}"/></a>`;
            });
            return text;
        };


        const addTicketPublicLink = (hash: string, isLegacy: boolean): void => {
            if (self.state != ProgressStateModel.Success || self.isSending || env.loggedUserId <= 0) {
                return;
            }
            if (isLegacy) {
                self.writtenMessage = self.writtenMessage + ` [[:${hash}]]`;
                return;
            }
            self.writtenMessage = self.writtenMessage + ` [[::${hash}]]`;
        }

        const addTicketPublicLinkAfterLoad = (hash: string, isLegacy: boolean): void => {
            forceTicketPublicLinkAfterLoad = { hash: hash, isLegacy: isLegacy };
        }

        const stopChat = (): void => {
            setCanShowChat(false);
        }

        const setCanShowChat = (value: boolean): void => {
            self.canShowChat = value;
        }

        return {
            setDataFromSubscribe,
            setErrorWhenSubscribing,
            setDataFromChange,
            addEmoticon,
            setWrittenMessage,
            sendMessageAsync,
            toggleBlockAllPrivateAsync,
            togglePrivate,
            setReplyTarget,
            clearReplayTarget,
            toggleBlockedPersonAsync,
            setIsLoading,
            addTicketPublicLink,
            addTicketPublicLinkAfterLoad,
            stopChat,
            setCanShowChat
        };
    });
